/* eslint-disable prettier/prettier */

import { faArrowAltToTop, faDna, faFileChartColumn, faUser, faUserCircle } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AppBar,
    Box,
    Button,
    Divider,
    Drawer,
    Fade,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
    darken,
    lighten,
    styled,
    useTheme
} from '@mui/material';
import React, { FunctionComponent, MouseEvent, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { Outlet, Link as RouteLink, useLocation, useParams } from 'react-router-dom';

import { Permission } from '@/apis/dtos';
import AppSettings from '@/AppSettings';
import BctLogo from '@/assets/images/bct-logo-horizontal.png';
import BctLogoDark from '@/assets/images/logo-h-w-new.png';
import Image from '@/components/layout/Image';
import AuthContext from '@/contexts/auth/AuthContext';
import ChosenThemeActionContext from '@/contexts/chosenThemeActionContext';
import LoadingContext from '@/contexts/data/LoadingContext';
import DefaultLayoutContext from '@/contexts/layout/DefaultLayoutContext';
import ChosenThemeContext from '@/contexts/styles/chosenThemeContext';
import ToggleFeedbackButton from '../buttons/ToggleFeedbackButton';
import OverlayLoading from '../common/overLayLoading/OverlayLoading';
import BreadcrumbContainer from './Breadcrumb/BreadcrumbContainer';

export interface ILink
{
    url: string;
    icon: any;
    newTab: boolean;
    route: boolean;
    title: string;
}

interface ILayoutProps {
    links?: ILink[];
    children?: ReactNode;
    breadcrumbs?: ReactNode;
}

const drawerWidth = '240px';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
      backgroundColor: theme.palette.mode === 'dark' ?
        '#121212' :
        'white',
        undefined
    
}))

const StyledToolbar = styled(Toolbar)(({ theme}) => ({
 
    gap: '112px',
    alignItems: 'stretch',
    height: '64px',
    justifyContent: 'space-between'
}))




const StyledTitle = styled(Typography)(({ theme}) => ({
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        theme.palette.primary.main,
    textDecoration: 'none',
    textTransform: 'uppercase'

}))

const StyledTitleLink = styled(Link)(({theme}) => ({
    marginRight: theme.spacing(1),
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    //flex: '1 1 auto'
}));

const LogoutContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

const DefaultLayout: FunctionComponent<ILayoutProps> = ({ links, children,  breadcrumbs }) => {
    const theme = useTheme();
    const location = useLocation();
    const { trialCode, participantCode } = useParams();

    const [trialLinkEnabled, setTrialLinkEnabled] = useState(false);

    const user = useContext(AuthContext);
    const { loading, validating }  = useContext(LoadingContext);

    const showDrawer = !!links && links.length > 0;

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    
    const TitleContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'hasBreadcrumbs' && prop !== 'hasHeading' })<{ hasBreadcrumbs: boolean, hasHeading: boolean }>(({ theme, hasHeading }) => ({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    paddingTop: hasHeading ? theme.spacing(8) : theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    }));

    const [drawOpen, setDrawOpen] = React.useState(false);

    const chosenTheme = useContext(ChosenThemeContext);
    const setChosenTheme = useContext(ChosenThemeActionContext);

    const changeTheme = useCallback(() => {
        setChosenTheme(chosenTheme === 'dark' ? 'light' : 'dark');
    }, [setChosenTheme, chosenTheme]);

    const activeLinkIndex = useMemo(() => {
        let i: number | undefined = undefined;
        let length: number = 0;

        if (!links || links.length === 0) {
            return i;
        }


        for (let ci = 0; ci < links.length; ci++) {
            const link = links[ci];

            if (location.pathname.startsWith(link.url) && link.url.length > length) {
                length = link.url.length;
                i = ci;
            }
        }

        return i;
    }, [links, location])

    const drawEnabled = showDrawer;

    let returnToTop = (
        <ListItem 
            button 
            sx={{
                borderTop: `1px solid ${theme.palette.grey[300]}`,
                minHeight: '50px'
            }}
            onClick={() => { 
                window.scrollTo({ left: 0, top: 0, behavior: 'smooth'});
            }}
        >
            <ListItemIcon 
                sx={{
                    fontSize: '24px'
                }}
            >
                <FontAwesomeIcon fixedWidth icon={faArrowAltToTop} />
            </ListItemIcon>
            <ListItemText 
                primary="Return to Top" 
                sx={{
                    display: !drawOpen ? 'none' : undefined
                }} 
            />
        </ListItem>
    );

    const { data: authData } = useContext(AuthContext);

    const contain = true;
    const BreadcrumbComponent = contain ? BreadcrumbContainer : breadcrumbs;
    
    const showReports = useMemo(() => {
        if (!authData) return undefined;

        return !!(authData.roles?.includes(Permission[Permission.OpmsAdminister]) || authData.roles?.includes(Permission[Permission.SystemAdminister]));
    }, [authData]);

    return (
        <DefaultLayoutContext.Provider value={{setTrialLinkEnabled: setTrialLinkEnabled}}>
            <Box 
                sx={{
                    display: 'flex',
                    width: '100%'
                }}
            >
                <StyledAppBar
                    position="fixed"
                >
                    <StyledToolbar>
                        {/* -- LOGO -- */}
                        <Box
                            sx={{
                                alignSelf: 'center',
                                lineHeight: 0,

                            }}
                            component={RouteLink}
                            to={AppSettings.trialPortalUrl}
                        >
                            <Image
                                sx={{
                                    height: 64
                                }}
                                alt="BCT Logo" 
                                src={  theme.palette.mode === 'dark' ? BctLogoDark : BctLogo }
                            />
                        </Box>

                        <Box
                            sx={{
                                marginLeft: theme.spacing(-13),
                                alignSelf: 'center',
                            }}

                        >
                            {/* -- HEADER TITLE -- */}
                            <StyledTitleLink>
                                <Button
                                    component={RouteLink}
                                    to={"/"}
                                >
                                    <StyledTitle 
                                        variant="h6"
                                    >
                                        Redact Verification
                                    </StyledTitle>
                                </Button>
                            </StyledTitleLink>
                        </Box>
                      

                        {/* -- HOME BUTTONS -- */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                width: '100%',
                            }}
                        >
                            {
                                showReports &&
                                <Button
                                    sx={{
                                        borderRadius: 0,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        height: '100%',
                                        color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main,
                                        backgroundPosition: 'bottom left',
                                        backgroundSize: `0% ${theme.spacing(0.75)}`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundColor:  theme.palette.mode === 'dark' ? '#262626' : '#fff',
                                        padding: `${theme.spacing(0, 1)} !important`,
                                        margin: theme.spacing(0),
                                        transition: 'background-size 0.15s, background-position 0s 0.15s',

                                        '& >span': {
                                            padding: theme.spacing(1),
                                            fontSize: '0.875rem',

                                            '& >span': {
                                                marginRight: '0.5rem !important',

                                                '& >svg': {
                                                    fontSize: '16px !important',
                                                }
                                            }
                                        },

                                        '&:hover': {
                                            backgroundPosition: 'bottom right',
                                            backgroundSize: `100% ${theme.spacing(0.75)}`,
                                            backgroundColor: 'rgba(169, 167, 167, 0.39)'
                                        }
                                    }}
                                    component={RouteLink}
                                    to='/reports'
                                    startIcon={<FontAwesomeIcon icon={faFileChartColumn} />}
                                >
                                    Reports
                                </Button>
                            }
                            {
                                (trialLinkEnabled || !!trialCode) && 
                                <Button
                                    sx={{
                                        borderRadius: 0,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        height: '100%',
                                        color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main,
                                        backgroundPosition: 'bottom left',
                                        backgroundSize: `0% ${theme.spacing(0.75)}`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundColor:  theme.palette.mode === 'dark' ? '#262626' : '#fff',
                                        padding: `${theme.spacing(0, 1)} !important`,
                                        margin: theme.spacing(0),
                                        transition: 'background-size 0.15s, background-position 0s 0.15s',

                                        '& >span': {
                                            padding: theme.spacing(1),
                                            fontSize: '0.875rem',

                                            '& >span': {
                                                marginRight: '0.5rem !important',

                                                '& >svg': {
                                                    fontSize: '16px !important',
                                                }
                                            }
                                        },

                                        '&:hover': {
                                            backgroundPosition: 'bottom right',
                                            backgroundSize: `100% ${theme.spacing(0.75)}`,
                                            backgroundColor: 'rgba(169, 167, 167, 0.39)'
                                        }
                                    }}
                                    component={RouteLink}
                                    to='/'
                                    startIcon={
                                        <FontAwesomeIcon icon={faDna} />
                                    }
                                >
                                    Trials
                                </Button>
                            }
                            
                            {
                                !!participantCode &&
                                <Button
                                    sx={{
                                        borderRadius: 0,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        height: '100%',
                                        color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main,
                                        backgroundPosition: 'bottom left',
                                        backgroundSize: `0% ${theme.spacing(0.75)}`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundColor: theme.palette.mode === 'dark' ? '#262626' : '#fff',
                                        padding: `${theme.spacing(0, 1)} !important`,
                                        margin: theme.spacing(0),
                                        transition: 'background-size 0.15s, background-position 0s 0.15s',

                                        '& >span': {
                                            padding: theme.spacing(1),
                                            fontSize: '0.875rem',

                                            '& >span': {
                                                marginRight: '0.5rem !important',

                                                '& >svg': {
                                                    fontSize: '16px !important',
                                                }
                                            }
                                        },

                                        '&:hover': {
                                            backgroundPosition: 'bottom right',
                                            backgroundSize: `100% ${theme.spacing(0.75)}`,
                                            backgroundColor: 'rgba(169, 167, 167, 0.39)'
                                        }
                                    }}
                                    component={RouteLink}
                                    to={`/${trialCode}`}
                                    startIcon={<FontAwesomeIcon color={theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.light}  icon={faUser} />}
                                >
                                    Participants
                                </Button>
                            }
                        </Box>
                        <LogoutContainer>

                             {/* -- SENTRY TOGGLE FEEDBACK -- */}
                            {AppSettings.sentry.dsn && AppSettings.sentry.enableReportABug && (
                                <ToggleFeedbackButton />
                            )}
                            {/* -- USER PROFILE -- */}
                            {
                                !!user && 
                                <>
                                    <IconButton
                                        id="btn-auth"
                                        aria-label="account of current user"
                                        aria-controls="menu-auth"
                                        aria-haspopup="true"
                                        onClick={handleMenu}
                                    >
                                        <FontAwesomeIcon  color={theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.light}   icon={faUserCircle} />
                                    </IconButton>
                                    <Menu
                                        id="menu-auth"
                                        anchorEl={anchorEl}
                                        keepMounted
                                        transformOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        open={anchorEl?.id === 'btn-auth'}
                                        onClose={handleClose}
                                        sx={{ mt: 5 }} // Added margin top to move it down
                                    >
                                        <MenuItem>{user.data?.displayName ?? user.data?.userName}</MenuItem>
                                        <Divider />
                                        {/* {isAdmin && <MenuItem component={RouteLink} to='/administration' onClick={handleClose}>Administration</MenuItem>} */}
                                        {/* <MenuItem component={RouteLink} to="/account/password" onClick={handleClose}>Change Password</MenuItem> */}
                                        <MenuItem component={"a"} href="/auth/logout">Logout</MenuItem>
                                    </Menu>
                                </>
                            }
                        </LogoutContainer>
                    </StyledToolbar>



                </StyledAppBar>




                {
                    drawEnabled && (
                        <Drawer
                            variant="permanent"
                            sx={{
                                width: drawOpen ? drawerWidth : 0,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                transition: drawOpen ? 
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.enteringScreen,
                                    }) :
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.leavingScreen,
                                    }),
                                overflowX: drawOpen ? 'hidden' : 'hidden',
                                [theme.breakpoints.up('sm')]: drawOpen ? 
                                    undefined : 
                                    {
                                        width: theme.spacing(8),
                                    },
                                '& .MuiPaper-root': {
                                    width: drawOpen ? drawerWidth : 0,
                                    transition: drawOpen ? 
                                        theme.transitions.create('width', {
                                            easing: theme.transitions.easing.sharp,
                                            duration: theme.transitions.duration.enteringScreen,
                                        }) :
                                        theme.transitions.create('width', {
                                            easing: theme.transitions.easing.sharp,
                                            duration: theme.transitions.duration.leavingScreen,
                                        }),
                                    overflowX: drawOpen ? 'hidden' : 'hidden',
                                    [theme.breakpoints.up('sm')]: drawOpen ? 
                                        undefined : 
                                        {
                                            width: theme.spacing(8),
                                        },
                                }
                            }}
                        >
                            <Box 
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    padding: theme.spacing(0, 1),
                                    // necessary for content to be below app bar
                                    ...(theme.mixins.toolbar as any),
                                    minHeight: '56px !important'
                                }}
                            />
                            <List disablePadding sx={{ display: 'flex', flexDirection: 'column', height: '100%'}}>
                                {
                                    links?.map((link, i) => {
                                        const active = i === activeLinkIndex;

                                        let linkComponent = null;
                                        if (link.newTab || !link.route) {
                                            linkComponent = (
                                                <ListItem 
                                                    button 
                                                    component="a" 
                                                    href={link.url} 
                                                    sx={{
                                                        borderBottom: `1px solid ${theme.palette.grey[300]}`,
                                                        minHeight: '50px'
                                                    }} 
                                                    key={i}
                                                >
                                                    <ListItemIcon 
                                                        sx={{
                                                            fontSize: '24px'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon fixedWidth icon={link.icon} />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={link.title} 
                                                        sx={{
                                                            display: !drawOpen ? 'none' : undefined
                                                        }} 
                                                    />
                                                </ListItem>
                                            );
                                        }
                                        else {
                                            linkComponent = (
                                                <ListItem 
                                                    button
                                                    component={RouteLink}
                                                    to={link.url}
                                                    sx={{
                                                        borderBottom: `1px solid ${theme.palette.grey[300]}`,
                                                        minHeight: '50px',
                                                        background: active ? theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.75) : lighten(theme.palette.common.black, 0.92) : undefined
                                                    }} 
                                                    key={i}
                                                >
                                                    <ListItemIcon 
                                                        sx={{
                                                            fontSize: '24px'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon fixedWidth icon={link.icon} />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={link.title} 
                                                        sx={{
                                                            display: !drawOpen ? 'none' : undefined
                                                        }} 
                                                    />
                                                </ListItem>
                                            );
                                        }


                                        if (drawOpen) {
                                            return linkComponent;
                                        }

                                        return (
                                            <Tooltip title={link.title} placement="right" key={i}>
                                                <Box>
                                                    {linkComponent}
                                                </Box>
                                            </Tooltip>
                                        );
                                    })
                                }
                                {returnToTop}
                            </List>
                        </Drawer>
                    )
                }
                <Box 
                    sx={{
                        flexGrow: 1,
                        width: '80%',
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Box 
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            padding: theme.spacing(0, 1),
                            // necessary for content to be below app bar
                            ...(theme.mixins.toolbar as any),
                            minHeight: '56px !important',
                            marginBottom: theme.spacing(10)
                        }}
                    />
                    { 
                        (loading || validating) && (
                            <OverlayLoading />
                        ) 
                    }
                

                    {/* <Fade appear in={routeLoaded}> */}
                    <Fade appear in={!loading}>
                        <Box 
                      

                            sx={{
                                // padding: theme.spacing(3),
                                // flex: '1 1 auto',
                                // display: 'flex',
                                // flexDirection: 'column',
                                padding: '24px',
                                maxWidth: '1280px',
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                margin: '0 auto'
                            }}
                        >
                                
                        <Box 
                            sx={{ 
                                position: 'absolute',
                                top: theme.spacing(0),
                                left: 0,
                                right: 0,
                                zIndex: theme.zIndex.appBar - 1,
                                pt: 8, 
                        
                            }}
                        >
                            <BreadcrumbContainer />
                        </Box>
                                 

                            <Outlet />
                            {children}
                        </Box>
                    </Fade>

                </Box>

            </Box>
            
        
        </DefaultLayoutContext.Provider>

    );
};

export default DefaultLayout;
